import type {
  AdminDashboardPoint,
  AdminDashboardStep,
} from "@/admin/entities/adminDashboard";
import {
  formatBucketLabel,
  formatPercent,
  formatUsdPrecise,
  revenueSharePercent,
} from "./dashboardFormat";

/** Ключ цвета палитры темы MUI: графики берут цвета из темы админки. */
export type ChartColorKey =
  | "primary"
  | "success"
  | "error"
  | "warning"
  | "info";

export interface DashboardSeries {
  id: string;
  type: "bar" | "line";
  label: string;
  data: number[];
  axis: "left" | "right";
  color: ChartColorKey;
}

export interface DashboardChartModel {
  labels: string[];
  series: DashboardSeries[];
}

const labelsOf = (series: AdminDashboardPoint[], step: AdminDashboardStep) =>
  series.map((point) => formatBucketLabel(point.date, step));

/** «Продажи и ревшер»: продажи столбцами (левая ось), ревшер линией (правая). */
export function buildSalesChart(
  series: AdminDashboardPoint[],
  step: AdminDashboardStep
): DashboardChartModel {
  return {
    labels: labelsOf(series, step),
    series: [
      {
        id: "sales",
        type: "bar",
        label: "Продажи клиентов",
        data: series.map((point) => point.salesUSD),
        axis: "left",
        color: "primary",
      },
      {
        id: "revenueShare",
        type: "line",
        label: "Наш ревшер",
        data: series.map((point) => point.revenueShareUSD),
        axis: "right",
        color: "warning",
      },
    ],
  };
}

/** «Деньги»: оплаченные счета и траты столбцами, чистый доход линией. */
export function buildMoneyChart(
  series: AdminDashboardPoint[],
  step: AdminDashboardStep
): DashboardChartModel {
  return {
    labels: labelsOf(series, step),
    series: [
      {
        id: "paid",
        type: "bar",
        label: "Оплачено мне",
        data: series.map((point) => point.paidInvoicesUSD),
        axis: "left",
        color: "success",
      },
      {
        id: "expenses",
        type: "bar",
        label: "Траты",
        data: series.map((point) => point.expensesUSD),
        axis: "left",
        color: "error",
      },
      {
        id: "netIncome",
        type: "line",
        label: "Чистый доход",
        data: series.map((point) => point.netIncomeUSD),
        axis: "left",
        color: "info",
      },
    ],
  };
}

/** «Просмотры»: просмотры и уникальные зрители линиями. */
export function buildViewsChart(
  series: AdminDashboardPoint[],
  step: AdminDashboardStep
): DashboardChartModel {
  return {
    labels: labelsOf(series, step),
    series: [
      {
        id: "views",
        type: "line",
        label: "Просмотры",
        data: series.map((point) => point.views),
        axis: "left",
        color: "primary",
      },
      {
        id: "viewers",
        type: "line",
        label: "Уникальные зрители",
        data: series.map((point) => point.viewers),
        axis: "left",
        color: "info",
      },
    ],
  };
}

/** Подсказка линии ревшера: сумма и процент от продаж того же шага. */
export function revenueShareTooltip(
  series: AdminDashboardPoint[],
  value: number | null,
  index: number
): string {
  if (value === null) return "";
  const point = series[index];
  const percent = point
    ? revenueSharePercent(point.revenueShareUSD, point.salesUSD)
    : null;
  const amount = formatUsdPrecise(value);
  return percent === null
    ? amount
    : `${amount}, ${formatPercent(percent)} от продаж`;
}

export function isSeriesEmpty(
  series: AdminDashboardPoint[],
  keys: (keyof AdminDashboardPoint)[]
): boolean {
  return series.every((point) => keys.every((key) => Number(point[key]) === 0));
}
